import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import type { CargoImage } from '../../types/quotes';

interface QuoteAttachmentsSectionProps {
  cargo_images: CargoImage[];
  handleDownloadAllImages: () => Promise<void>;
  handleDownloadImage: (imageId: number) => Promise<void>;
  formatBytes: (bytes: number) => string;
}

export const QuoteAttachmentsSection: React.FC<QuoteAttachmentsSectionProps> = ({
  cargo_images,
  handleDownloadAllImages,
  handleDownloadImage,
  formatBytes,
}) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">{t('admin.quote.cargo_images')}</CardTitle>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDownloadAllImages}
          >
            {t('admin.quote.download_all_files')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {cargo_images.map((img) => {
            const isPdf = img.content_type === 'application/pdf' || img.filename.toLowerCase().endsWith('.pdf');
            return (
              <div key={img.id} className="border rounded-md overflow-hidden bg-white">
                <a
                  href={img.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-square relative group overflow-hidden bg-slate-100"
                >
                  {isPdf ? (
                    <div className="w-full h-full flex items-center justify-center text-slate-600">
                      <span className="text-sm font-medium">{t('common.pdf')}</span>
                    </div>
                  ) : (
                    <img
                      src={img.thumb_url || img.url}
                      alt={t('admin.quote.cargo_image_alt')}
                      className="object-cover w-full h-full transition-transform group-hover:scale-105"
                    />
                  )}
                </a>

                <div className="p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" title={img.filename}>
                      {img.filename}
                    </div>
                    <div className="text-xs text-slate-500">
                      {formatBytes(img.byte_size)}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadImage(img.id)}
                  >
                    {t('admin.quote.download_file')}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
